import { useMemo, useState } from 'react';
import { Alert, Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { api } from '@web/lib/api';
import { ProblemAlert, applyServerErrors } from '@web/ui/ProblemAlert';
import { newPasswordSchema, type NewPasswordValues } from '../passwordSchema';
import { AuthLayout } from './AuthLayout';
import { NewPasswordFields } from './LoginPage';

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Anchor component={Link} to="/login" size="sm" ta="center">
      {t('common:auth.backToSignIn')}
    </Anchor>
  );
}

function SuccessBox({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <Stack>
      <Alert color="green" icon={<IconCircleCheck aria-hidden />} role="status">
        {message}
      </Alert>
      <Button component={Link} to="/login">
        {t('common:auth.signIn')}
      </Button>
    </Stack>
  );
}

/** Shared "choose a password with a token" form (activation + reset). */
function TokenPasswordForm({ endpoint, submitLabel, successMessage }: { endpoint: string; submitLabel: string; successMessage: string }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const schema = useMemo(() => newPasswordSchema(), [t]);
  const form = useForm<NewPasswordValues>({ resolver: zodResolver(schema), defaultValues: { password: '', confirm: '' } });
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Stack>
        <Alert color="red">{t('common:auth.tokenMissing')}</Alert>
        <BackToLogin />
      </Stack>
    );
  }
  if (done) return <SuccessBox message={successMessage} />;

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit(async (v) => {
        setError(null);
        try {
          await api.post(endpoint, { token, password: v.password });
          setDone(true);
        } catch (e) {
          if (!applyServerErrors(e, form.setError)) setError(e);
        }
      })}
    >
      <Stack gap="sm">
        {error ? <ProblemAlert error={error} /> : null}
        <NewPasswordFields form={form} />
        <Button type="submit" loading={form.formState.isSubmitting}>
          {submitLabel}
        </Button>
        <BackToLogin />
      </Stack>
    </form>
  );
}

/** `/activate?token=` — invitation acceptance (72 h link). */
export function ActivatePage() {
  const { t } = useTranslation();
  return (
    <AuthLayout title={t('common:auth.activateTitle')} subtitle={t('common:auth.activateHint')}>
      <TokenPasswordForm endpoint="/auth/activate" submitLabel={t('common:auth.activate')} successMessage={t('common:auth.activated')} />
    </AuthLayout>
  );
}

/** `/reset-password?token=` */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  return (
    <AuthLayout title={t('common:auth.resetTitle')}>
      <TokenPasswordForm endpoint="/auth/password/reset" submitLabel={t('common:auth.resetPassword')} successMessage={t('common:auth.resetDone')} />
    </AuthLayout>
  );
}

/** `/forgot-password` — always answers with the same neutral confirmation (no account enumeration). */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const schema = useMemo(() => z.object({ login: z.string().trim().min(1, t('common:validation.required')) }), [t]);
  const form = useForm<{ login: string }>({ resolver: zodResolver(schema), defaultValues: { login: '' } });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <AuthLayout title={t('common:auth.forgotTitle')} subtitle={t('common:auth.forgotHint')}>
      {sent ? (
        <Stack>
          <Alert color="green" icon={<IconCircleCheck aria-hidden />} role="status">
            {t('common:auth.forgotSent')}
          </Alert>
          <BackToLogin />
        </Stack>
      ) : (
        <form
          noValidate
          onSubmit={form.handleSubmit(async (v) => {
            setError(null);
            try {
              await api.post('/auth/password/forgot', { login: v.login });
              setSent(true);
            } catch (e) {
              setError(e);
            }
          })}
        >
          <Stack gap="sm">
            {error ? <ProblemAlert error={error} /> : null}
            <TextInput
              label={t('common:auth.login')}
              dir="ltr"
              autoComplete="username"
              autoFocus
              required
              error={form.formState.errors.login?.message}
              {...form.register('login')}
            />
            <Button type="submit" loading={form.formState.isSubmitting}>
              {t('common:auth.sendResetLink')}
            </Button>
            <Text ta="center">
              <BackToLogin />
            </Text>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}
